import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { SelectChangeEvent, SelectFilterEvent, SelectLazyLoadEvent } from 'primeng/select';

// Composants
import { BaseComponent } from 'src/app/@shared/components/abstract/base.component';

@Component({
    selector: 'biifor-select-base',
    template: `
        NO UI TO BE FOUND HERE!
    `,
    standalone: false
})
export abstract class SelectBaseComponent<T> extends BaseComponent implements ControlValueAccessor {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/


    public abstract placeholder: string;
    @Input() public appendTo: string = 'body';
    @Input() public filter: boolean = true;
    @Input() public showClear: boolean = false;

    // Évènements propagés depuis le p-select interne (mêmes noms et payloads que PrimeNG)
    // (alias 'onBlur' pour éviter la collision avec la méthode onBlur héritée de BaseComponent)
    @Output('onBlur') public onBlurEmitter: EventEmitter<Event> = new EventEmitter<Event>();
    @Output() public onChange: EventEmitter<SelectChangeEvent> = new EventEmitter<SelectChangeEvent>();
    @Output() public onClear: EventEmitter<Event> = new EventEmitter<Event>();
    @Output() public onClick: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
    @Output() public onFilter: EventEmitter<SelectFilterEvent> = new EventEmitter<SelectFilterEvent>();
    @Output() public onFocus: EventEmitter<Event> = new EventEmitter<Event>();
    @Output() public onHide: EventEmitter<any> = new EventEmitter<any>();
    @Output() public onLazyLoad: EventEmitter<SelectLazyLoadEvent> = new EventEmitter<SelectLazyLoadEvent>();
    @Output() public onShow: EventEmitter<any> = new EventEmitter<any>();

    public options: T[] = [];
    public value: T | null = null;

    protected _onChange: (value: T | null) => void = () => { };
    protected _onTouched: () => void = () => { };

    /*----------------------------------------------------------------------------------------
     * Cycle de vie
     ----------------------------------------------------------------------------------------*/

    protected override loadData(): void {
        this._loadOptions();
    }

    /*----------------------------------------------------------------------------------------
     * ControlValueAccessor
     ----------------------------------------------------------------------------------------*/

    public writeValue(value: T | null): void {
        this.value = value;
    }

    public registerOnChange(fn: (value: T | null) => void): void {
        this._onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques (relais d'évènements depuis le p-select interne)
     ----------------------------------------------------------------------------------------*/

    public emitOnBlur(event: Event): void {
        this._onTouched();
        this.onBlurEmitter.emit(event);
    }

    public emitOnChange(event: SelectChangeEvent): void {
        this._onChange(this.value);
        this._onTouched();
        this.onChange.emit(event);
    }

    public emitOnClear(event: Event): void {
        this.onClear.emit(event);
    }

    public emitOnClick(event: MouseEvent): void {
        this.onClick.emit(event);
    }

    public emitOnFilter(event: SelectFilterEvent): void {
        this.onFilter.emit(event);
    }

    public emitOnFocus(event: Event): void {
        this.onFocus.emit(event);
    }

    public emitOnHide(event: any): void {
        this.onHide.emit(event);
    }

    public emitOnLazyLoad(event: SelectLazyLoadEvent): void {
        this.onLazyLoad.emit(event);
    }

    public emitOnShow(event: any): void {
        this.onShow.emit(event);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected abstract _loadOptions(): void;
}
